import { SettlementConceptBreakdown } from '../../../models/Entities/Settlements/SettlementConceptBreakdown';

/** Lógica del desglose por concepto, sin React. */

export const conceptLabel = (item: Pick<SettlementConceptBreakdown, 'conceptId' | 'conceptName'>): string => {
    const name = (item.conceptName || '').trim();
    if (name) return name;
    return item.conceptId ? `Concepto #${item.conceptId}` : 'Sin concepto';
};

export const currencyOf = (item: Pick<SettlementConceptBreakdown, 'currency'>): string => (item.currency || '').trim().toUpperCase() || 'ARS';

export interface GroupTotals {
    quantity: number;
    settled: number;
    included: number;
    amount: number;
    centerWithholding: number;
    teamWithholding: number;
    centerFee: number;
    teamFee: number;
    amountToCollect: number;
}

export interface CurrencyGroup {
    currency: string;
    items: SettlementConceptBreakdown[];
    totals: GroupTotals;
}

/**
 * Agrupa por moneda, cada grupo de mayor a menor cobrado. Los conceptos que
 * se liquidan dentro de otro van después del que los contiene, para que la
 * resta se lea en orden: visita 900, y debajo viáticos 100 "en Visita".
 */
export const groupByCurrency = (list: SettlementConceptBreakdown[] | null | undefined): CurrencyGroup[] => {
    const groups = new Map<string, SettlementConceptBreakdown[]>();
    (list ?? []).forEach((item) => {
        const currency = currencyOf(item);
        groups.set(currency, [...(groups.get(currency) ?? []), item]);
    });

    return Array.from(groups.entries())
        .map(([currency, items]) => ({ currency, items: orderWithinCurrency(items), totals: totalsOf(items) }))
        .sort((a, b) => a.currency.localeCompare(b.currency));
};

const orderWithinCurrency = (items: SettlementConceptBreakdown[]): SettlementConceptBreakdown[] => {
    const byAmount = (a: SettlementConceptBreakdown, b: SettlementConceptBreakdown) =>
        (b.amount - a.amount) || conceptLabel(a).localeCompare(conceptLabel(b), undefined, { sensitivity: 'base', numeric: true });

    const standalone = items.filter((item) => !item.billedInConceptId).sort(byAmount);
    const included = items.filter((item) => !!item.billedInConceptId).sort(byAmount);
    const placed = new Set<SettlementConceptBreakdown>();
    const result: SettlementConceptBreakdown[] = [];

    standalone.forEach((parent) => {
        result.push(parent);
        included
            .filter((child) => child.billedInConceptId === parent.conceptId && !placed.has(child))
            .forEach((child) => { result.push(child); placed.add(child); });
    });
    // Incluidos cuyo contenedor no está en la lista: al final, sin perderlos.
    included.filter((child) => !placed.has(child)).forEach((child) => result.push(child));
    return result;
};

const num = (value: number | null | undefined): number => (typeof value === 'number' && Number.isFinite(value) ? value : 0);

export const totalsOf = (items: SettlementConceptBreakdown[]): GroupTotals =>
    items.reduce<GroupTotals>(
        (acc, item) => ({
            quantity: acc.quantity + num(item.quantity),
            settled: acc.settled + num(item.settledAmount),
            included: acc.included + num(item.includedConceptsAmount),
            amount: acc.amount + num(item.amount),
            centerWithholding: acc.centerWithholding + num(item.centerWithholding),
            teamWithholding: acc.teamWithholding + num(item.teamWithholding),
            centerFee: acc.centerFee + num(item.centerFee),
            teamFee: acc.teamFee + num(item.teamFee),
            amountToCollect: acc.amountToCollect + num(item.amountToCollect)
        }),
        { quantity: 0, settled: 0, included: 0, amount: 0, centerWithholding: 0, teamWithholding: 0, centerFee: 0, teamFee: 0, amountToCollect: 0 }
    );

/** Hay división de honorarios o retenciones en algún concepto: si no, esas columnas sobran. */
export const hasFeeSplit = (items: SettlementConceptBreakdown[]): boolean =>
    items.some((item) => num(item.teamFee) !== 0 || num(item.centerWithholding) !== 0 || num(item.teamWithholding) !== 0 || (num(item.centerFee) !== 0 && num(item.centerFee) !== num(item.amount)));

/** Participación de un concepto en lo cobrado de su moneda, 0-100. */
export const shareOf = (item: SettlementConceptBreakdown, group: CurrencyGroup): number =>
    group.totals.amount > 0 ? Math.max(0, Math.min(100, (item.amount / group.totals.amount) * 100)) : 0;
